/**
 * Deck Template v2 field contract (PowerPoint wave 2): browser copy.
 *
 * Mirrors cloud-run-worker/deckFields.js exactly (same code, dependency-free, no DOM), checked by
 * deckFields.parity.test.mjs. Both copies must behave identically: the worker writes the
 * content at import, the editor edits it, and both normalise it the same way so a template
 * saves and reloads identically.
 *
 * Content shape (`templates.content`, JSON):
 *   {
 *     schema: 'quill.deck-template', version: 2,
 *     template_type: 'powerpoint', template_name, sourceFileName,
 *     sourceRevision, sourceSha256, slideSize: { cx, cy } (EMU), styleKitId,
 *     brief: { audience, purpose, narrative },
 *     theme: { colors: { name: 'RRGGBB' }, fonts: { major, minor }, clrMap },
 *     render: { status: ready|pending|failed, bucket, prefix, rev, pageCount, pages[{ w, h }],
 *               substitutedFonts[], error },
 *     manifestPath,
 *     slides: [{ key: 'sld<sldId>', sldId, sourceIndex, hidden, layout: { key, name }, role,
 *                purpose, include: always|optional|repeat|exclude, excludeReason, excludeOrigin,
 *                repeat: { min, max, unit } | null, notes: { mode, instruction },
 *                literalOverrides: { shapeKey: text }, render: { page, png, thumb } }],
 *     shapes: [{ key: 'sld<sldId>/sp<cNvPr id>', slideKey, id, name, kind, phType, phIdx,
 *                bbox: { x, y, cx, cy, rot?, flipH?, flipV? } (EMU), font: { face, sizePt }, capacity, paragraphs,
 *                levels, action: ai_field|fixed|variable|optional|clear, variable, fieldId,
 *                locked, ruleReason, sampleHash, samplePreview }],
 *     fields: { f_<hash>: { id, shapeKey, slideKey, kind, instruction, source, required,
 *                limits: { maxChars, maxLines, maxBullets, maxCharsPerBullet, maxRows },
 *                fit, origin: rule|ai|user, state: suggested|accepted, edited, confidence } },
 *     droppedSlides: [{ slideKey, sourceIndex, reason, origin }]   (derived from slides)
 *   }
 *
 * Field ids: 'f_' + 8 lowercase hex of 32-bit FNV-1a (UTF-16 code units) over
 * 'deck:' + shapeKey. A collision inside one deck is resolved deterministically: keys are
 * taken in sorted order and a later key retries with '#2', '#3', ... appended.
 */

export const DECK_SCHEMA = 'quill.deck-template';
export const DECK_VERSION = 2;

/** What happens to a shape at generation. */
export const SHAPE_ACTIONS = Object.freeze(['ai_field', 'fixed', 'variable', 'optional', 'clear']);
/** Actions that carry a field spec. */
export const FIELD_ACTIONS = Object.freeze(['ai_field', 'optional']);
/** Shape kinds the manifest reports. */
export const SHAPE_KINDS = Object.freeze(['text', 'shape', 'table', 'picture', 'chart', 'smartart', 'media', 'ole', 'connector', 'group', 'other']);
/** Field value kinds (image and chart_data are reserved for later waves). */
export const DECK_FIELD_KINDS = Object.freeze(['text', 'list', 'rich', 'table_rows', 'image', 'chart_data']);
/** Evidence sources (same as the Word field contract). */
export const DECK_FIELD_SOURCES = Object.freeze(['requirements', 'sessions', 'project', 'any']);
/** Fit policies. */
export const FIT_POLICIES = Object.freeze(['shrink_then_split', 'shrink', 'shorten_only']);
export const FIELD_ORIGINS = Object.freeze(['rule', 'ai', 'user']);
export const FIELD_STATES = Object.freeze(['suggested', 'accepted']);
export const SLIDE_ROLES = Object.freeze([
  'title', 'agenda', 'section', 'content', 'comparison', 'table', 'timeline', 'team', 'quote', 'closing', 'appendix', 'other',
]);
export const INCLUDE_RULES = Object.freeze(['always', 'optional', 'repeat', 'exclude']);
export const REPEAT_UNITS = Object.freeze(['requirement', 'epic', 'session', 'ai']);
export const NOTES_MODES = Object.freeze(['keep', 'clear', 'ai']);
/** Code variables (resolved by code, never by the model). Same list as templateFields.js. */
export const DECK_VARIABLES = Object.freeze(['project.name', 'customer.name', 'doc.date', 'doc.version', 'doc.title']);

/** Sample text sent to the model or exposed to tools is cut to this many characters. */
export const MAX_SAMPLE_CHARS = 300;
/** Short preview kept on each shape in the template row. */
export const SAMPLE_PREVIEW_CHARS = 160;
export const MAX_INSTRUCTION_CHARS = 900;
export const MAX_PURPOSE_CHARS = 400;
export const MAX_REASON_CHARS = 300;
export const MAX_BRIEF_PART_CHARS = 700;
export const MAX_REPEAT = 50;

/** Overlap check: this many consecutive identical words with the sample text is a copy. */
export const OVERLAP_MIN_WORDS = 6;

/** Progress values written by the deck import job, and the visible steps. */
export const DECK_IMPORT_PROGRESS = Object.freeze({ reading: 12, rendering: 18, suggesting: 25, saving: 92 });
export const DECK_IMPORT_STEPS = Object.freeze([
  Object.freeze({ key: 'uploaded', label: 'Uploaded' }),
  Object.freeze({ key: 'reading', label: 'Reading slides' }),
  Object.freeze({ key: 'rendering', label: 'Rendering' }),
  Object.freeze({ key: 'suggesting', label: 'Suggesting AI fields' }),
  Object.freeze({ key: 'ready', label: 'Ready' }),
]);

// 'sld<sldId>/sp<cNvPr id>', '-<n>' for a repeated id on one slide, 'x<n>' for a shape without an id.
const SHAPE_KEY_RE = /^sld(\d{1,10})\/(?:sp(\d{1,10})(?:-(\d{1,4}))?|x(\d{1,6}))$/;
const SLIDE_KEY_RE = /^sld(\d{1,10})$/;
const FIELD_ID_RE = /^f_[0-9a-f]{8}$/;
const HEX_COLOR_RE = /^[0-9A-Fa-f]{6}$/;
const MAX_EMU = 51206400 * 4;

// ---------------------------------------------------------------------------
// Hashing and keys
// ---------------------------------------------------------------------------

/**
 * 32-bit FNV-1a over UTF-16 code units.
 * @param {string} input - String to hash.
 * @returns {number} Unsigned 32-bit hash.
 */
export function fnv1a32(input) {
  let hash = 0x811c9dc5;
  const str = String(input ?? '');
  for (let i = 0; i < str.length; i += 1) {
    hash ^= str.charCodeAt(i);
    hash = Math.imul(hash, 0x01000193) >>> 0;
  }
  return hash >>> 0;
}

/**
 * FNV-1a as 8 lowercase hex characters.
 * @param {string} input - String to hash.
 * @returns {string}
 */
export function fnv1aHex(input) {
  return fnv1a32(input).toString(16).padStart(8, '0');
}

/**
 * Slide key for a sldId.
 * @param {number|string} sldId - p:sldId id.
 * @returns {string}
 */
export function slideKeyFor(sldId) {
  return `sld${sldId}`;
}

/**
 * Shape key for a slide and a cNvPr id.
 * @param {number|string} sldId - p:sldId id.
 * @param {number|string} cNvPrId - p:cNvPr id.
 * @returns {string}
 */
export function shapeKeyFor(sldId, cNvPrId) {
  return `sld${sldId}/sp${cNvPrId}`;
}

/**
 * Parse a shape key.
 * @param {unknown} key - Candidate.
 * @returns {{ sldId: number, spId: number|null, slideKey: string }|null}
 */
export function parseShapeKey(key) {
  const m = SHAPE_KEY_RE.exec(String(key ?? ''));
  if (!m) return null;
  return { sldId: Number(m[1]), spId: m[2] === undefined ? null : Number(m[2]), slideKey: `sld${m[1]}` };
}

/**
 * True for a slide key.
 * @param {unknown} key - Candidate.
 * @returns {boolean}
 */
export function isSlideKey(key) {
  return SLIDE_KEY_RE.test(String(key ?? ''));
}

/**
 * Field id for a shape key (before collision handling).
 * @param {string} shapeKey - Shape key.
 * @param {number} [attempt] - 1 for the plain id, 2+ after a collision.
 * @returns {string}
 */
export function deckFieldId(shapeKey, attempt = 1) {
  const base = `deck:${shapeKey}`;
  return `f_${fnv1aHex(attempt > 1 ? `${base}#${attempt}` : base)}`;
}

/**
 * Collision-free field ids for a deck's shape keys.
 * @param {string[]} shapeKeys - Shape keys (any order; duplicates ignored).
 * @returns {Map<string, string>} shapeKey -> field id.
 */
export function assignDeckFieldIds(shapeKeys) {
  const keys = [...new Set((Array.isArray(shapeKeys) ? shapeKeys : []).map(String))].sort();
  const taken = new Set();
  const out = new Map();
  for (const key of keys) {
    let attempt = 1;
    let id = deckFieldId(key, attempt);
    while (taken.has(id)) {
      attempt += 1;
      id = deckFieldId(key, attempt);
    }
    taken.add(id);
    out.set(key, id);
  }
  return out;
}

// ---------------------------------------------------------------------------
// Text helpers
// ---------------------------------------------------------------------------

/**
 * Whitespace-collapsed, trimmed text (NBSP and line breaks become spaces).
 * @param {unknown} value - Text.
 * @returns {string}
 */
export function normalizeText(value) {
  return String(value ?? '').replace(/\s+/g, ' ').trim();
}

/**
 * Cut text to a length on a word boundary when possible.
 * @param {unknown} value - Text.
 * @param {number} max - Maximum characters.
 * @returns {string}
 */
export function clipText(value, max) {
  const text = normalizeText(value);
  if (text.length <= max) return text;
  const cut = text.slice(0, max);
  const space = cut.lastIndexOf(' ');
  return (space > max * 0.6 ? cut.slice(0, space) : cut).trim();
}

/**
 * Stable hash of sample text (whitespace and case folded).
 * @param {unknown} text - Sample text.
 * @returns {string} 8 hex characters, or '' for empty text.
 */
export function sampleHash(text) {
  const t = normalizeText(text).toLowerCase();
  return t ? fnv1aHex(t) : '';
}

/**
 * Words with their character spans, lowercased (letters and digits of any script).
 * @param {string} text - Text.
 * @returns {{ word: string, start: number, end: number }[]}
 */
export function wordSpans(text) {
  const out = [];
  const re = /[\p{L}\p{N}]+/gu;
  const s = String(text ?? '');
  let m = re.exec(s);
  while (m) {
    out.push({ word: m[0].toLowerCase(), start: m.index, end: m.index + m[0].length });
    m = re.exec(s);
  }
  return out;
}

/**
 * Set of every run of `n` consecutive words in the samples.
 * @param {string[]} samples - Sample texts.
 * @param {number} n - Run length.
 * @returns {Set<string>}
 */
export function sampleWordGrams(samples, n = OVERLAP_MIN_WORDS) {
  const grams = new Set();
  for (const sample of Array.isArray(samples) ? samples : [samples]) {
    const words = wordSpans(sample).map((w) => w.word);
    for (let i = 0; i + n <= words.length; i += 1) grams.add(words.slice(i, i + n).join(' '));
  }
  return grams;
}

/**
 * Spans of an instruction that repeat at least `minWords` consecutive words of the samples.
 * @param {string} instruction - Instruction text.
 * @param {string[]|Set<string>} samples - Sample texts, or a gram set from sampleWordGrams.
 * @param {{ minWords?: number }} [options] - Run length.
 * @returns {{ start: number, end: number, words: number }[]} Character spans, in order, merged.
 */
export function findVerbatimOverlaps(instruction, samples, { minWords = OVERLAP_MIN_WORDS } = {}) {
  const grams = samples instanceof Set ? samples : sampleWordGrams(samples, minWords);
  const words = wordSpans(instruction);
  if (!grams.size || words.length < minWords) return [];
  const covered = new Array(words.length).fill(false);
  for (let i = 0; i + minWords <= words.length; i += 1) {
    const gram = words.slice(i, i + minWords).map((w) => w.word).join(' ');
    if (grams.has(gram)) for (let j = i; j < i + minWords; j += 1) covered[j] = true;
  }
  const spans = [];
  let i = 0;
  while (i < words.length) {
    if (!covered[i]) {
      i += 1;
      continue;
    }
    let j = i;
    while (j + 1 < words.length && covered[j + 1]) j += 1;
    spans.push({ start: words[i].start, end: words[j].end, words: j - i + 1 });
    i = j + 1;
  }
  return spans;
}

/**
 * Remove copied sample text from an instruction. When too little of the instruction survives,
 * the fallback instruction is used instead.
 * @param {string} instruction - Instruction text.
 * @param {string[]|Set<string>} samples - Sample texts or a gram set.
 * @param {{ minWords?: number, fallback?: string }} [options] - Run length and replacement.
 * @returns {{ text: string, stripped: boolean, replaced: boolean, copiedWords: number }}
 */
export function stripVerbatimOverlap(instruction, samples, { minWords = OVERLAP_MIN_WORDS, fallback = '' } = {}) {
  const original = String(instruction ?? '');
  const spans = findVerbatimOverlaps(original, samples, { minWords });
  if (!spans.length) return { text: original, stripped: false, replaced: false, copiedWords: 0 };
  let text = '';
  let cursor = 0;
  for (const span of spans) {
    text += `${original.slice(cursor, span.start)} `;
    cursor = span.end;
  }
  text += original.slice(cursor);
  text = normalizeText(text.replace(/["'\u201c\u201d\u2018\u2019]\s*["'\u201c\u201d\u2018\u2019]/g, ' ').replace(/\s+([,.;:!?])/g, '$1').replace(/[,;:]+([.!?])/g, '$1'))
    .replace(/^[,.;:!?\s]+/, '')
    .replace(/\(\s*\)/g, '')
    .trim();
  const copiedWords = spans.reduce((n, s) => n + s.words, 0);
  const remainingWords = wordSpans(text).length;
  const tooLittle = remainingWords < 4 || remainingWords < copiedWords;
  if (tooLittle) return { text: normalizeText(fallback), stripped: true, replaced: true, copiedWords };
  return { text, stripped: true, replaced: false, copiedWords };
}

// ---------------------------------------------------------------------------
// Limits
// ---------------------------------------------------------------------------

/**
 * Positive integer or null.
 * @param {unknown} value - Candidate.
 * @param {number} [max] - Upper bound.
 * @returns {number|null}
 */
function posInt(value, max = 1000000) {
  const n = Number(value);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.min(max, Math.floor(n));
}

/**
 * Field limits from a shape's capacity.
 * @param {object|null} capacity - { maxChars, maxLines, maxBullets, maxCharsPerBullet, rows }.
 * @param {string} kind - Field kind.
 * @returns {{ maxChars: number|null, maxLines: number|null, maxBullets: number|null, maxCharsPerBullet: number|null, maxRows: number|null }}
 */
export function limitsFromCapacity(capacity, kind) {
  const c = capacity && typeof capacity === 'object' ? capacity : {};
  const isList = kind === 'list';
  const isTable = kind === 'table_rows';
  return {
    maxChars: isTable ? null : posInt(c.maxChars),
    maxLines: isTable ? null : posInt(c.maxLines, 1000),
    maxBullets: isList ? posInt(c.maxBullets, 1000) : null,
    maxCharsPerBullet: isList ? posInt(c.maxCharsPerBullet) : null,
    maxRows: isTable ? posInt(c.maxRows ?? c.rows, 1000) : null,
  };
}

/**
 * Normalise limits and keep each one at or below the capacity limit (when there is one).
 * @param {object|null} limits - Proposed limits.
 * @param {object|null} capacityLimits - Limits from limitsFromCapacity.
 * @returns {{ maxChars: number|null, maxLines: number|null, maxBullets: number|null, maxCharsPerBullet: number|null, maxRows: number|null }}
 */
export function clampLimits(limits, capacityLimits) {
  const l = limits && typeof limits === 'object' ? limits : {};
  const cap = capacityLimits && typeof capacityLimits === 'object' ? capacityLimits : {};
  const out = {};
  for (const key of ['maxChars', 'maxLines', 'maxBullets', 'maxCharsPerBullet', 'maxRows']) {
    const proposed = posInt(l[key]);
    const ceiling = posInt(cap[key]);
    if (proposed && ceiling) out[key] = Math.min(proposed, ceiling);
    else out[key] = proposed || ceiling || null;
  }
  return out;
}

/**
 * Default fit policy for a field.
 * @param {string} kind - Field kind.
 * @param {string|null} phType - Placeholder type.
 * @returns {string}
 */
export function defaultFitFor(kind, phType) {
  if (phType === 'title' || phType === 'ctrTitle') return 'shorten_only';
  if (kind === 'list' || kind === 'table_rows') return 'shrink_then_split';
  return 'shrink';
}

// ---------------------------------------------------------------------------
// Normalisation
// ---------------------------------------------------------------------------

/**
 * Value from an allow-list, else the fallback.
 * @param {unknown} value - Candidate.
 * @param {ReadonlyArray<string>} list - Allowed values.
 * @param {string|null} fallback - Default.
 * @returns {string|null}
 */
function oneOf(value, list, fallback) {
  return typeof value === 'string' && list.includes(value) ? value : fallback;
}

/**
 * Integer in [min, max] or the fallback.
 * @param {unknown} value - Candidate.
 * @param {number} min - Lower bound.
 * @param {number} max - Upper bound.
 * @param {number|null} fallback - Default.
 * @returns {number|null}
 */
function intIn(value, min, max, fallback) {
  if (value === null || value === undefined || value === '' || typeof value === 'boolean') return fallback;
  const n = Number(value);
  if (!Number.isFinite(n)) return fallback;
  return Math.max(min, Math.min(max, Math.round(n)));
}

/**
 * Plain object or an empty one.
 * @param {unknown} value - Candidate.
 * @returns {object}
 */
function obj(value) {
  return value && typeof value === 'object' && !Array.isArray(value) ? value : {};
}

/**
 * Parse stored content (string or object). Returns null unless it is a Deck Template v2.
 * @param {unknown} raw - templates.content.
 * @returns {object|null}
 */
export function parseDeckTemplate(raw) {
  let value = raw;
  if (typeof raw === 'string') {
    if (!raw.includes(DECK_SCHEMA)) return null;
    try {
      value = JSON.parse(raw);
    } catch {
      return null;
    }
  }
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  return value.schema === DECK_SCHEMA ? value : null;
}

/**
 * True when stored content is a Deck Template v2.
 * @param {unknown} raw - templates.content (string or object).
 * @returns {boolean}
 */
export function isDeckTemplate(raw) {
  return parseDeckTemplate(raw) !== null;
}

/**
 * Normalised bounding box in EMU, or null.
 * The frame is unrotated; rot (degrees clockwise about the centre, 0 to 360) and flipH/flipV are
 * kept only when set.
 * @param {unknown} value - { x, y, cx, cy, rot?, flipH?, flipV? }.
 * @returns {{ x: number, y: number, cx: number, cy: number, rot?: number, flipH?: boolean, flipV?: boolean }|null}
 */
function normalizeBbox(value) {
  const b = obj(value);
  const nums = ['x', 'y', 'cx', 'cy'].map((k) => Number(b[k]));
  if (nums.some((n) => !Number.isFinite(n))) return null;
  const [x, y, cx, cy] = nums.map((n) => Math.round(n));
  if (cx < 0 || cy < 0 || Math.abs(x) > MAX_EMU || Math.abs(y) > MAX_EMU || cx > MAX_EMU || cy > MAX_EMU) return null;
  const out = { x, y, cx, cy };
  const rotRaw = Number(b.rot);
  const rot = Number.isFinite(rotRaw) ? Math.round((((rotRaw % 360) + 360) % 360) * 1000) / 1000 : 0;
  if (rot && rot !== 360) out.rot = rot;
  if (b.flipH === true) out.flipH = true;
  if (b.flipV === true) out.flipV = true;
  return out;
}

/**
 * Normalised capacity facts (numbers only).
 * @param {unknown} value - Capacity from the manifest.
 * @returns {object|null}
 */
function normalizeCapacity(value) {
  const c = obj(value);
  const out = {};
  for (const key of ['charsPerLine', 'maxLines', 'maxChars', 'maxBullets', 'maxCharsPerBullet', 'maxRows']) {
    const n = posInt(c[key]);
    if (n) out[key] = n;
  }
  if (c.approximate === true) out.approximate = true;
  return Object.keys(out).length ? out : null;
}

/**
 * Normalise one slide.
 * @param {object} raw - Slide.
 * @param {number} position - Array index (default sourceIndex).
 * @returns {object|null}
 */
function normalizeSlide(raw, position) {
  const s = obj(raw);
  if (!isSlideKey(s.key)) return null;
  const include = oneOf(s.include, INCLUDE_RULES, 'always');
  const repeatRaw = obj(s.repeat);
  const min = intIn(repeatRaw.min, 0, MAX_REPEAT, 1);
  const max = intIn(repeatRaw.max, 1, MAX_REPEAT, Math.max(1, min));
  const notes = obj(s.notes);
  const layout = obj(s.layout);
  const render = obj(s.render);
  return {
    key: s.key,
    sldId: intIn(s.sldId ?? String(s.key).slice(3), 0, 2147483647, 0),
    sourceIndex: intIn(s.sourceIndex, 0, 100000, position),
    hidden: s.hidden === true,
    layout: {
      key: typeof layout.key === 'string' ? layout.key.slice(0, 200) : null,
      name: typeof layout.name === 'string' ? clipText(layout.name, 200) : null,
    },
    role: oneOf(s.role, SLIDE_ROLES, 'content'),
    purpose: clipText(s.purpose, MAX_PURPOSE_CHARS),
    include,
    excludeReason: include === 'exclude' ? clipText(s.excludeReason, MAX_REASON_CHARS) || 'Excluded' : '',
    excludeOrigin: include === 'exclude' ? oneOf(s.excludeOrigin, FIELD_ORIGINS, 'user') : null,
    repeat: include === 'repeat' ? { min, max: Math.max(min, max), unit: oneOf(repeatRaw.unit, REPEAT_UNITS, 'ai') } : null,
    notes: { mode: oneOf(notes.mode, NOTES_MODES, 'keep'), instruction: clipText(notes.instruction, MAX_INSTRUCTION_CHARS) },
    literalOverrides: {},
    render: {
      page: intIn(render.page, 1, 100000, null),
      png: typeof render.png === 'string' && render.png ? render.png.slice(0, 500) : null,
      thumb: typeof render.thumb === 'string' && render.thumb ? render.thumb.slice(0, 500) : null,
    },
    _overrides: obj(s.literalOverrides),
  };
}

/**
 * Normalise one shape.
 * @param {object} raw - Shape.
 * @param {Set<string>} slideKeys - Known slide keys.
 * @returns {object|null}
 */
function normalizeShape(raw, slideKeys) {
  const s = obj(raw);
  const parsed = parseShapeKey(s.key);
  if (!parsed || !slideKeys.has(parsed.slideKey)) return null;
  const font = obj(s.font);
  const sizePt = Number(font.sizePt);
  let action = oneOf(s.action, SHAPE_ACTIONS, 'fixed');
  let variable = null;
  if (action === 'variable') {
    variable = oneOf(s.variable, DECK_VARIABLES, null);
    if (!variable) action = 'fixed';
  }
  return {
    key: s.key,
    slideKey: parsed.slideKey,
    id: parsed.spId,
    name: typeof s.name === 'string' ? s.name.slice(0, 200) : '',
    kind: oneOf(s.kind, SHAPE_KINDS, 'other'),
    phType: typeof s.phType === 'string' && /^[A-Za-z]{1,20}$/.test(s.phType) ? s.phType : null,
    phIdx: intIn(s.phIdx, 0, 2147483647, null),
    bbox: normalizeBbox(s.bbox),
    font: {
      face: typeof font.face === 'string' && font.face ? font.face.slice(0, 120) : null,
      sizePt: Number.isFinite(sizePt) && sizePt > 0 ? Math.round(sizePt * 100) / 100 : null,
    },
    capacity: normalizeCapacity(s.capacity),
    paragraphs: intIn(s.paragraphs, 0, 10000, 0),
    levels: Array.isArray(s.levels) ? [...new Set(s.levels.map((n) => intIn(n, 0, 8, 0)))].sort((a, b) => a - b) : [],
    action,
    variable,
    fieldId: null,
    locked: s.locked === true,
    ruleReason: typeof s.ruleReason === 'string' && s.ruleReason ? s.ruleReason.slice(0, 60) : null,
    sampleHash: typeof s.sampleHash === 'string' && /^[0-9a-f]{8}$/.test(s.sampleHash) ? s.sampleHash : '',
    samplePreview: clipText(s.samplePreview, SAMPLE_PREVIEW_CHARS),
  };
}

/**
 * Normalise one field for its shape.
 * @param {object} raw - Field.
 * @param {object} shape - Normalised shape.
 * @param {string} id - Field id.
 * @returns {object}
 */
function normalizeField(raw, shape, id) {
  const f = obj(raw);
  const defaultKind = shape.kind === 'table' ? 'table_rows' : 'text';
  const kind = oneOf(f.kind, DECK_FIELD_KINDS, defaultKind);
  const capacityLimits = limitsFromCapacity(shape.capacity, kind);
  const confidence = f.confidence === null || f.confidence === undefined || f.confidence === '' ? NaN : Number(f.confidence);
  return {
    id,
    shapeKey: shape.key,
    slideKey: shape.slideKey,
    kind,
    instruction: clipText(f.instruction, MAX_INSTRUCTION_CHARS),
    source: oneOf(f.source, DECK_FIELD_SOURCES, 'any'),
    required: shape.action === 'optional' ? false : f.required !== false,
    limits: clampLimits(f.limits, capacityLimits),
    fit: oneOf(f.fit, FIT_POLICIES, defaultFitFor(kind, shape.phType)),
    origin: oneOf(f.origin, FIELD_ORIGINS, 'user'),
    state: oneOf(f.state, FIELD_STATES, 'suggested'),
    // True once the owner changed a rule or AI suggestion (prompt, kind, source, limits...), so
    // "accepted unchanged" can be told from "edited then accepted".
    edited: f.edited === true,
    confidence: Number.isFinite(confidence) ? Math.max(0, Math.min(1, Math.round(confidence * 100) / 100)) : null,
  };
}

/**
 * Normalised colour map / font tokens.
 * @param {unknown} value - Theme tokens.
 * @returns {{ colors: Record<string, string>, fonts: { major: string|null, minor: string|null }, clrMap: Record<string, string> }}
 */
function normalizeTheme(value) {
  const t = obj(value);
  const colors = {};
  for (const [name, val] of Object.entries(obj(t.colors)).sort(([a], [b]) => a.localeCompare(b))) {
    if (/^[A-Za-z0-9]{1,20}$/.test(name) && typeof val === 'string' && HEX_COLOR_RE.test(val)) colors[name] = val.toUpperCase();
  }
  const clrMap = {};
  for (const [name, val] of Object.entries(obj(t.clrMap)).sort(([a], [b]) => a.localeCompare(b))) {
    if (/^[A-Za-z0-9]{1,20}$/.test(name) && typeof val === 'string' && /^[A-Za-z0-9]{1,20}$/.test(val)) clrMap[name] = val;
  }
  const fonts = obj(t.fonts);
  const face = (v) => (typeof v === 'string' && v.trim() ? v.trim().slice(0, 120) : null);
  return { colors, fonts: { major: face(fonts.major), minor: face(fonts.minor) }, clrMap };
}

/**
 * Normalised render summary.
 * @param {unknown} value - Render facts.
 * @returns {object}
 */
function normalizeRender(value) {
  const r = obj(value);
  return {
    status: oneOf(r.status, ['ready', 'pending', 'failed'], 'pending'),
    bucket: typeof r.bucket === 'string' && r.bucket ? r.bucket.slice(0, 100) : null,
    prefix: typeof r.prefix === 'string' && r.prefix ? r.prefix.slice(0, 400) : null,
    rev: typeof r.rev === 'string' && r.rev ? r.rev.slice(0, 100) : null,
    pageCount: intIn(r.pageCount, 0, 100000, 0),
    pages: Array.isArray(r.pages)
      ? r.pages.slice(0, 5000).map((p) => ({ w: intIn(obj(p).w, 0, 100000, 0), h: intIn(obj(p).h, 0, 100000, 0) }))
      : [],
    substitutedFonts: Array.isArray(r.substitutedFonts)
      ? [...new Set(r.substitutedFonts.filter((f) => typeof f === 'string' && f).map((f) => f.slice(0, 120)))].sort()
      : [],
    error: typeof r.error === 'string' && r.error ? r.error.slice(0, MAX_REASON_CHARS) : null,
  };
}

/**
 * Normalise a Deck Template v2 (idempotent: normalising the result again changes nothing, so a
 * template saves and reloads identically). Unknown values fall back to defaults, references to
 * unknown slides/shapes are dropped, fields exist exactly for ai_field/optional shapes, and
 * droppedSlides is rebuilt from the slides.
 * @param {unknown} raw - Content (object or JSON string).
 * @returns {object|null} Normalised content, or null when it is not a deck template.
 */
export function normalizeDeckTemplate(raw) {
  const c = parseDeckTemplate(raw);
  if (!c) return null;
  const slides = [];
  const seenSlides = new Set();
  (Array.isArray(c.slides) ? c.slides : []).forEach((s, i) => {
    const slide = normalizeSlide(s, i);
    if (slide && !seenSlides.has(slide.key)) {
      seenSlides.add(slide.key);
      slides.push(slide);
    }
  });
  const shapes = [];
  const seenShapes = new Set();
  for (const s of Array.isArray(c.shapes) ? c.shapes : []) {
    const shape = normalizeShape(s, seenSlides);
    if (shape && !seenShapes.has(shape.key)) {
      seenShapes.add(shape.key);
      shapes.push(shape);
    }
  }

  const rawFields = obj(c.fields);
  const byShape = new Map();
  for (const [id, f] of Object.entries(rawFields)) {
    const key = obj(f).shapeKey;
    if (typeof key === 'string' && !byShape.has(key)) byShape.set(key, { id, field: f });
  }
  const fieldShapes = shapes.filter((s) => FIELD_ACTIONS.includes(s.action));
  // Existing ids are kept when they are well formed and unique; new ones come from the hash.
  const assigned = assignDeckFieldIds(fieldShapes.map((s) => s.key));
  const usedIds = new Set();
  const fields = {};
  for (const shape of fieldShapes) {
    const existing = byShape.get(shape.key);
    let id = existing && FIELD_ID_RE.test(existing.id) && !usedIds.has(existing.id) ? existing.id : assigned.get(shape.key);
    if (usedIds.has(id)) {
      let attempt = 2;
      while (usedIds.has(deckFieldId(shape.key, attempt))) attempt += 1;
      id = deckFieldId(shape.key, attempt);
    }
    usedIds.add(id);
    shape.fieldId = id;
    fields[id] = normalizeField(existing ? existing.field : {}, shape, id);
  }

  const shapeSlide = new Map(shapes.map((s) => [s.key, s.slideKey]));
  for (const slide of slides) {
    const overrides = {};
    for (const [key, text] of Object.entries(slide._overrides).sort(([a], [b]) => a.localeCompare(b))) {
      if (shapeSlide.get(key) === slide.key && typeof text === 'string') overrides[key] = text.slice(0, 5000);
    }
    slide.literalOverrides = overrides;
    delete slide._overrides;
  }

  const size = obj(c.slideSize);
  const brief = obj(c.brief);
  return {
    schema: DECK_SCHEMA,
    version: DECK_VERSION,
    template_type: 'powerpoint',
    template_name: clipText(c.template_name, 200),
    sourceFileName: typeof c.sourceFileName === 'string' ? c.sourceFileName.slice(0, 260) : '',
    sourceRevision: intIn(c.sourceRevision, 1, 100000, 1),
    sourceSha256: typeof c.sourceSha256 === 'string' && /^[0-9a-f]{64}$/.test(c.sourceSha256) ? c.sourceSha256 : null,
    slideSize: { cx: intIn(size.cx, 1, MAX_EMU, 12192000), cy: intIn(size.cy, 1, MAX_EMU, 6858000) },
    styleKitId: typeof c.styleKitId === 'string' && c.styleKitId ? c.styleKitId.slice(0, 64) : null,
    brief: {
      audience: clipText(brief.audience, MAX_BRIEF_PART_CHARS),
      purpose: clipText(brief.purpose, MAX_BRIEF_PART_CHARS),
      narrative: clipText(brief.narrative, MAX_BRIEF_PART_CHARS),
    },
    theme: normalizeTheme(c.theme),
    render: normalizeRender(c.render),
    manifestPath: typeof c.manifestPath === 'string' && c.manifestPath ? c.manifestPath.slice(0, 500) : null,
    slides,
    shapes,
    fields,
    droppedSlides: slides
      .filter((s) => s.include === 'exclude')
      .map((s) => ({ slideKey: s.key, sourceIndex: s.sourceIndex, reason: s.excludeReason, origin: s.excludeOrigin })),
  };
}

/**
 * Problems that make a deck template unusable or inconsistent (empty when fine).
 * @param {unknown} raw - Content.
 * @returns {string[]}
 */
export function validateDeckTemplate(raw) {
  const c = parseDeckTemplate(raw);
  if (!c) return ['The content is not a deck template.'];
  const problems = [];
  if (c.version !== DECK_VERSION) problems.push(`Unsupported deck template version ${c.version}.`);
  const slides = Array.isArray(c.slides) ? c.slides : [];
  const shapes = Array.isArray(c.shapes) ? c.shapes : [];
  const fields = obj(c.fields);
  if (!slides.length) problems.push('The deck template has no slides.');
  const slideKeys = new Set();
  for (const s of slides) {
    if (!isSlideKey(s?.key)) problems.push('A slide has no valid key.');
    else if (slideKeys.has(s.key)) problems.push(`Slide ${s.key} appears twice.`);
    else slideKeys.add(s.key);
  }
  const shapeKeys = new Set();
  const fieldIdsSeen = new Set();
  for (const s of shapes) {
    const parsed = parseShapeKey(s?.key);
    if (!parsed) {
      problems.push('A shape has no valid key.');
      continue;
    }
    if (shapeKeys.has(s.key)) problems.push(`Shape ${s.key} appears twice.`);
    shapeKeys.add(s.key);
    if (!slideKeys.has(parsed.slideKey)) problems.push(`Shape ${s.key} is on an unknown slide.`);
    if (!SHAPE_ACTIONS.includes(s.action)) problems.push(`Shape ${s.key} has no action.`);
    if (s.action === 'variable' && !DECK_VARIABLES.includes(s.variable)) problems.push(`Shape ${s.key} names an unknown variable.`);
    if (FIELD_ACTIONS.includes(s.action)) {
      const field = fields[s.fieldId];
      if (!field) problems.push(`Shape ${s.key} has no field.`);
      else if (field.shapeKey !== s.key) problems.push(`Field ${s.fieldId} belongs to another shape.`);
      if (fieldIdsSeen.has(s.fieldId)) problems.push(`Field id ${s.fieldId} is used twice.`);
      fieldIdsSeen.add(s.fieldId);
    }
  }
  for (const [id, f] of Object.entries(fields)) {
    if (!FIELD_ID_RE.test(id)) problems.push(`Field id ${id} is not valid.`);
    if (f?.id !== id) problems.push(`Field ${id} has a mismatched id.`);
    if (!shapeKeys.has(f?.shapeKey)) problems.push(`Field ${id} points at an unknown shape.`);
    if (!DECK_FIELD_KINDS.includes(f?.kind)) problems.push(`Field ${id} has an unknown kind.`);
    if (!FIELD_STATES.includes(f?.state)) problems.push(`Field ${id} has an unknown state.`);
  }
  return problems;
}

// ---------------------------------------------------------------------------
// Editing helpers (pure; return new content)
// ---------------------------------------------------------------------------

/**
 * Deep copy of JSON content.
 * @param {object} value - Content.
 * @returns {object}
 */
function clone(value) {
  return JSON.parse(JSON.stringify(value));
}

/**
 * Accept suggested fields (all, or one slide's).
 * @param {object} content - Normalised content.
 * @param {{ slideKey?: string }} [options] - Limit to one slide.
 * @returns {object} New content.
 */
export function acceptSuggestions(content, { slideKey } = {}) {
  const next = clone(content);
  for (const field of Object.values(obj(next.fields))) {
    if (field.state === 'suggested' && (!slideKey || field.slideKey === slideKey)) field.state = 'accepted';
  }
  return next;
}

/**
 * Change what a shape does. Moving to ai_field/optional creates or keeps its field; moving away
 * removes the field (the editor's undo keeps the old model).
 * @param {object} content - Normalised content.
 * @param {string} shapeKey - Shape key.
 * @param {string} action - New action.
 * @param {{ variable?: string, field?: object }} [options] - Variable name, or field values.
 * @returns {object} New normalised content.
 */
export function setShapeAction(content, shapeKey, action, { variable, field } = {}) {
  const next = clone(content);
  const shape = (next.shapes || []).find((s) => s.key === shapeKey);
  if (!shape || !SHAPE_ACTIONS.includes(action)) return normalizeDeckTemplate(next);
  shape.action = action;
  shape.variable = action === 'variable' ? variable ?? shape.variable : null;
  if (FIELD_ACTIONS.includes(action)) {
    const id = shape.fieldId && next.fields?.[shape.fieldId] ? shape.fieldId : null;
    const current = id ? next.fields[id] : { origin: 'user', state: 'accepted' };
    const merged = { ...current, ...obj(field), shapeKey };
    if (!id) {
      next.fields = { ...obj(next.fields), [`f_pending_${shapeKey}`]: merged };
    } else {
      next.fields[id] = merged;
    }
  }
  return normalizeDeckTemplate(next);
}

/**
 * Reject a suggested field: the shape keeps its source text (fixed).
 * @param {object} content - Normalised content.
 * @param {string} fieldId - Field id.
 * @returns {object} New normalised content.
 */
export function rejectSuggestion(content, fieldId) {
  const field = obj(content?.fields)[fieldId];
  if (!field) return normalizeDeckTemplate(content);
  return setShapeAction(content, field.shapeKey, 'fixed');
}

/**
 * Put a dropped slide back into the deck.
 * @param {object} content - Normalised content.
 * @param {string} slideKey - Slide key.
 * @returns {object} New normalised content.
 */
export function restoreSlide(content, slideKey) {
  const next = clone(content);
  const slide = (next.slides || []).find((s) => s.key === slideKey);
  if (slide && slide.include === 'exclude') {
    slide.include = 'always';
    slide.excludeReason = '';
  }
  return normalizeDeckTemplate(next);
}

/**
 * Counts for summaries and job results.
 * @param {object} content - Normalised content.
 * @returns {{ slides: number, included: number, dropped: number, shapes: number, textShapes: number, actions: Record<string, number>, fields: number, suggested: number, accepted: number }}
 */
export function deckCounts(content) {
  const slides = Array.isArray(content?.slides) ? content.slides : [];
  const shapes = Array.isArray(content?.shapes) ? content.shapes : [];
  const fields = Object.values(obj(content?.fields));
  const actions = Object.fromEntries(SHAPE_ACTIONS.map((a) => [a, 0]));
  for (const s of shapes) if (actions[s.action] !== undefined) actions[s.action] += 1;
  return {
    slides: slides.length,
    included: slides.filter((s) => s.include !== 'exclude').length,
    dropped: slides.filter((s) => s.include === 'exclude').length,
    shapes: shapes.length,
    textShapes: shapes.filter((s) => s.kind === 'text' || s.kind === 'table').length,
    actions,
    fields: fields.length,
    suggested: fields.filter((f) => f.state === 'suggested').length,
    accepted: fields.filter((f) => f.state === 'accepted').length,
  };
}

/**
 * Visible import step for an ai_requests progress value (deck imports).
 * @param {number} progress - 0..100.
 * @param {string} [status] - ai_requests status.
 * @returns {'uploaded'|'reading'|'rendering'|'suggesting'|'ready'}
 */
export function deckImportStepForProgress(progress, status = 'processing') {
  if (status === 'completed') return 'ready';
  if (status !== 'processing') return 'uploaded';
  const p = Number(progress) || 0;
  if (p >= DECK_IMPORT_PROGRESS.suggesting) return 'suggesting';
  if (p >= DECK_IMPORT_PROGRESS.rendering) return 'rendering';
  return 'reading';
}
